import { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  computeQuote, money, ROOF_LABELS, FREQ_LABELS, ADDON_LABELS, ADDONS,
} from "../lib/pricing";
import { Slider } from "./ui/slider";
import { Switch } from "./ui/switch";
import { Button } from "./ui/button";
import { Zap, TrendingUp, ShieldCheck, ArrowRight } from "lucide-react";

const ROOF_OPTS = ["single", "two_story", "steep"];
const FREQ_OPTS = ["one_time", "biannual", "quarterly"];

export function PricingCalculator({ onBook }) {
  const navigate = useNavigate();
  const [panels, setPanels] = useState(16);
  const [roof, setRoof] = useState("single");
  const [freq, setFreq] = useState("one_time");
  const [addons, setAddons] = useState([]);
  const [vet, setVet] = useState(false);

  const q = useMemo(() => computeQuote(panels, roof, freq, addons, vet), [panels, roof, freq, addons, vet]);

  const toggleAddon = (a) =>
    setAddons((prev) => (prev.includes(a) ? prev.filter((x) => x !== a) : [...prev, a]));

  const handleBook = () => {
    const config = { panels, roof_type: roof, frequency: freq, addons, is_veteran: vet };
    if (onBook) onBook(config);
    else navigate("/book", { state: config });
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-5 gap-6 lg:gap-8" data-testid="pricing-calculator">
      {/* controls */}
      <div className="lg:col-span-3 bg-white rounded-2xl border border-slate-200 p-6 sm:p-8 shadow-sm">
        <div className="mb-8">
          <div className="flex items-end justify-between mb-3">
            <label className="font-heading font-bold text-slate-900">Number of Solar Panels</label>
            <span className="font-mono text-2xl font-bold text-emerald-700" data-testid="calc-panel-count">{panels}</span>
          </div>
          <Slider
            value={[panels]} min={8} max={60} step={1}
            onValueChange={(v) => setPanels(v[0])}
            data-testid="pricing-calculator-slider"
          />
          <div className="flex justify-between text-xs text-slate-400 mt-2 font-medium"><span>8</span><span>60+</span></div>
        </div>

        <div className="mb-8">
          <label className="font-heading font-bold text-slate-900 block mb-3">Roof Type</label>
          <div className="grid grid-cols-3 gap-2">
            {ROOF_OPTS.map((r) => (
              <button key={r} onClick={() => setRoof(r)} data-testid={`calc-roof-${r}`}
                className={`px-3 py-3 rounded-xl text-sm font-semibold border transition-colors ${roof === r ? "bg-emerald-700 text-white border-emerald-700" : "bg-white text-slate-600 border-slate-200 hover:border-emerald-400"}`}>
                {ROOF_LABELS[r]}
              </button>
            ))}
          </div>
        </div>

        <div className="mb-8">
          <label className="font-heading font-bold text-slate-900 block mb-3">Cleaning Frequency</label>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
            {FREQ_OPTS.map((f) => (
              <button key={f} onClick={() => setFreq(f)} data-testid={`calc-freq-${f}`}
                className={`px-3 py-3 rounded-xl text-sm font-semibold border transition-colors ${freq === f ? "bg-emerald-700 text-white border-emerald-700" : "bg-white text-slate-600 border-slate-200 hover:border-emerald-400"}`}>
                {FREQ_LABELS[f]}
              </button>
            ))}
          </div>
        </div>

        <div className="mb-8">
          <label className="font-heading font-bold text-slate-900 block mb-3">Add-Ons</label>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {Object.keys(ADDONS).map((a) => (
              <button key={a} onClick={() => toggleAddon(a)} data-testid={`calc-addon-${a}`}
                className={`flex items-center justify-between px-4 py-3 rounded-xl text-sm font-semibold border transition-colors ${addons.includes(a) ? "bg-emerald-50 text-emerald-800 border-emerald-400" : "bg-white text-slate-600 border-slate-200 hover:border-emerald-400"}`}>
                <span>{ADDON_LABELS[a]}</span><span className="font-mono">+{money(ADDONS[a])}</span>
              </button>
            ))}
          </div>
        </div>

        <div className="flex items-center justify-between bg-slate-900 rounded-xl px-5 py-4">
          <div className="flex items-center gap-3">
            <ShieldCheck className="w-5 h-5 text-amber-400" />
            <div>
              <div className="text-white font-bold text-sm">Veteran / First Responder</div>
              <div className="text-slate-400 text-xs">15% off your first cleaning</div>
            </div>
          </div>
          <Switch checked={vet} onCheckedChange={setVet} data-testid="veteran-discount-toggle" />
        </div>
      </div>

      {/* summary */}
      <div className="lg:col-span-2 bg-slate-900 rounded-2xl p-6 sm:p-8 text-white flex flex-col grain">
        <div className="text-xs font-bold uppercase tracking-[0.2em] text-emerald-400 mb-6">Live Estimate</div>

        <div className="grid grid-cols-2 gap-3 mb-6">
          <div className="bg-white/5 rounded-xl p-4 border border-white/10">
            <Zap className="w-5 h-5 text-amber-400 mb-2" />
            <div className="text-2xl font-black font-mono" data-testid="calc-output-gain">+{q.outputGain}%</div>
            <div className="text-xs text-slate-400">Output restored</div>
          </div>
          <div className="bg-white/5 rounded-xl p-4 border border-white/10">
            <TrendingUp className="w-5 h-5 text-emerald-400 mb-2" />
            <div className="text-2xl font-black font-mono" data-testid="calc-annual-savings">{money(q.annualSavings)}</div>
            <div className="text-xs text-slate-400">Est. annual savings</div>
          </div>
        </div>

        <div className="space-y-2.5 text-sm border-t border-white/10 pt-5">
          <div className="flex justify-between text-slate-300"><span>Base service</span><span className="font-mono">{money(q.base)}</span></div>
          {q.addonTotal > 0 && <div className="flex justify-between text-slate-300"><span>Add-ons</span><span className="font-mono">{money(q.addonTotal)}</span></div>}
          {q.discount > 0 && (
            <div className="flex justify-between text-amber-400 font-semibold" data-testid="calc-vet-discount-line">
              <span>Veteran discount (15%)</span><span className="font-mono">−{money(q.discount)}</span>
            </div>
          )}
        </div>

        <div className="flex items-end justify-between border-t border-white/10 mt-4 pt-5 mb-6">
          <span className="text-slate-300 font-semibold">Total</span>
          <span className="text-4xl font-black font-mono text-white" data-testid="calc-total">{money(q.total)}</span>
        </div>

        <Button onClick={handleBook} data-testid="calc-book-btn"
          className="w-full rounded-full bg-emerald-600 hover:bg-emerald-500 text-white font-bold h-12 gap-2 mt-auto">
          Book This Cleaning <ArrowRight className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );
}
